import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowPathIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  SpotImage,
  SpotImagesResponse,
  SpotSearchResult,
} from "@/common/model/spotimages/SpotImage";
import { SpotImageService } from "@/services/SpotImageService";
import SpotSearchInput from "@/components/spotimages/SpotSearchInput";
import CoverImagePreview from "@/components/spotimages/CoverImagePreview";
import ImageGallery from "@/components/spotimages/ImageGallery";
import AvailableImagesSection from "@/components/spotimages/AvailableImagesSection";
import ImageUploadButton from "@/components/spotimages/ImageUploadButton";
import SpotInfoPanel from "@/components/spotimages/SpotInfoPanel";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const SpotImagesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedSpot, setSelectedSpot] = useState<SpotSearchResult | null>(null);
  const [spotData, setSpotData] = useState<SpotImagesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Get spot ID from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const spotId = searchParams.get("spotId");

    if (spotId) {
      loadSpotData(parseInt(spotId));
    }
  }, [location.search]);

  const loadSpotData = async (spotId: number) => {
    setIsLoading(true);
    try {
      const data = await SpotImageService.getImages(spotId);
      setSpotData(data);
      setSelectedSpot({
        id: data.spotId,
        name: data.spotName,
        latitude: 0,
        longitude: 0,
        countryId: 0,
        countryName: "",
        alpha2: "",
      });
    } catch (error) {
      console.error("Error loading spot data:", error);
      toast.error("Failed to load spot images");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpotSelect = useCallback(
    (spot: SpotSearchResult) => {
      setSelectedSpot(spot);
      navigate(`/spot-images?spotId=${spot.id}`, { replace: true });
      loadSpotData(spot.id);
    },
    [navigate]
  );

  const handleReorder = async (orderedFilenames: string[]) => {
    if (!selectedSpot) return;

    try {
      await SpotImageService.updateOrder(selectedSpot.id, orderedFilenames);
      toast.success("Image order updated");
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update image order");
      loadSpotData(selectedSpot.id);
      throw error;
    }
  };

  const handleSetCover = async (filename: string) => {
    if (!selectedSpot) return;

    try {
      await SpotImageService.setCover(selectedSpot.id, filename);
      toast.success("Cover image updated");
      loadSpotData(selectedSpot.id);
    } catch (error) {
      console.error("Error setting cover:", error);
      toast.error("Failed to set cover image");
    }
  };

  const handleAddToSelection = async (filename: string) => {
    if (!selectedSpot) return;

    try {
      await SpotImageService.addToSelection(selectedSpot.id, filename);
      toast.success("Image added to selection");
      loadSpotData(selectedSpot.id);
    } catch (error) {
      console.error("Error adding to selection:", error);
      toast.error("Failed to add image to selection");
    }
  };

  const handleRemoveFromSelection = async (filename: string) => {
    if (!selectedSpot) return;

    try {
      await SpotImageService.removeFromSelection(selectedSpot.id, filename);
      toast.success("Image removed from selection");
      loadSpotData(selectedSpot.id);
    } catch (error) {
      console.error("Error removing from selection:", error);
      toast.error("Failed to remove image from selection");
    }
  };

  const handleDeleteImage = async (filename: string) => {
    if (!selectedSpot) return;

    try {
      await SpotImageService.deletePermanently(selectedSpot.id, filename);
      toast.success("Image deleted permanently");
      loadSpotData(selectedSpot.id);
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  const handleUploadImage = async (file: File) => {
    if (!selectedSpot) return;

    // Upload and add to selection by default
    await SpotImageService.uploadImage(selectedSpot.id, file, true);
    loadSpotData(selectedSpot.id);
  };

  const handleResetToMl = async () => {
    if (!selectedSpot) return;

    setIsResetting(true);
    try {
      await SpotImageService.resetToMlRankings(selectedSpot.id);
      toast.success("Manual override removed. ML can now update rankings.");
      loadSpotData(selectedSpot.id);
    } catch (error) {
      console.error("Error resetting to ML:", error);
      toast.error("Failed to reset to ML rankings");
    } finally {
      setIsResetting(false);
      setShowResetConfirm(false);
    }
  };

  const getCoverImage = (): SpotImage | null => {
    if (!spotData?.coverImage) return null;
    return (
      spotData.selectedImages.find((img) => img.filename === spotData.coverImage) || null
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Spot Images</h1>
          <p className="text-gray-600 mt-1">
            Manage images for spot discovery pages
          </p>
        </div>
        <SpotSearchInput
          onSpotSelect={handleSpotSelect}
          selectedSpot={selectedSpot}
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      )}

      {!isLoading && !selectedSpot && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">
            Search and select a spot to manage its images
          </p>
        </div>
      )}

      {!isLoading && selectedSpot && spotData && (
        <div className="space-y-8">
          {/* Header with spot info and actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  {spotData.spotName}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
                  <span>Spot ID: {spotData.spotId}</span>
                  <span>|</span>
                  <span>
                    {spotData.selectedImages.length} selected / {spotData.totalImagesInFolder} total
                  </span>
                  {spotData.lastModified && (
                    <>
                      <span>|</span>
                      <span>
                        Modified: {new Date(spotData.lastModified).toLocaleString()}
                      </span>
                    </>
                  )}
                  {spotData.lastModifiedBy && (
                    <span>by {spotData.lastModifiedBy}</span>
                  )}
                </div>

                {/* Manual override warning */}
                {spotData.manualOverride && (
                  <div className="mt-4 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        Manual Override Active
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        ML pipeline will not update this spot's rankings.
                        <button
                          onClick={() => setShowResetConfirm(true)}
                          className="ml-2 text-yellow-800 underline hover:no-underline"
                        >
                          Reset to allow ML updates
                        </button>
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <ImageUploadButton
                  onUpload={handleUploadImage}
                  disabled={!selectedSpot}
                />
                <button
                  onClick={() => loadSpotData(selectedSpot.id)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Spot Info Panel and Cover Image - Two column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Spot Info with Map */}
            <SpotInfoPanel
              spotId={spotData.spotId}
              spotName={spotData.spotName}
            />

            {/* Cover image preview */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Cover Image
              </h3>
              <CoverImagePreview
                coverImage={getCoverImage()}
                coverStaticUrl={spotData.coverStaticUrl}
                spotName={spotData.spotName}
              />
            </div>
          </div>

          {/* Selected images gallery */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Selected Images ({spotData.selectedImages.length})
              </h3>
              <p className="text-sm text-gray-500">
                Drag to reorder. Click to preview. Star to set as cover.
              </p>
            </div>
            <ImageGallery
              images={spotData.selectedImages}
              coverImage={spotData.coverImage}
              onReorder={handleReorder}
              onSetCover={handleSetCover}
              onRemoveFromSelection={handleRemoveFromSelection}
              onDelete={handleDeleteImage}
            />
          </div>

          {/* Available images section */}
          <AvailableImagesSection
            images={spotData.availableImages}
            onAddToSelection={handleAddToSelection}
            onDelete={handleDeleteImage}
          />
        </div>
      )}

      <ConfirmDialog
        isOpen={showResetConfirm}
        onCancel={() => setShowResetConfirm(false)}
        onConfirm={handleResetToMl}
        title="Reset to ML Rankings"
        message="This will allow the ML pipeline to update this spot's image rankings again. Your manual changes may be overwritten by the next ML run."
        confirmText={isResetting ? "Resetting..." : "Reset"}
        type="info"
      />
    </div>
  );
};

export default SpotImagesPage;
